/**
 * src/services/shareNotifyService.ts
 * =============================================================================
 * "Avísame cuando comenten": guarda la dirección de correo a la que mandar los
 * avisos de un envío anónimo.
 *
 * Se llama desde el modal de compartir, justo después de que el usuario tenga
 * el enlace en la mano. Ese es el momento: acaba de mandar un plano y lo que
 * quiere saber es cuándo le contestan.
 *
 * El recuerdo de que ya lo activó vive en localStorage y no en el servidor,
 * porque preguntarlo obligaría a una ruta más que devuelve el correo de otro a
 * quien tenga el enlace. Si el usuario cambia de navegador volverá a ver la
 * casilla; darle a guardar otra vez es inofensivo (el servidor no reescribe una
 * dirección ya puesta).
 * =============================================================================
 */

import { api } from '@/lib/api'

function clave(token: string): string {
  return `ploter:share:${token}:aviso`
}

/**
 * Clave de remitente: el secreto que demuestra que ESTE navegador fue el que
 * subió el archivo.
 *
 * Hace falta porque quien sube y quien recibe abren exactamente la misma
 * página con el mismo enlace: no hay nada más que los distinga. Sin esto, el
 * destinatario podía apuntar su dirección y quedarse con los avisos —y con
 * ellos, con un correo que lleva dentro el enlace al PDF.
 *
 * Se genera al subir (`uploadService`) y no sale de aquí. Si el usuario cambia
 * de navegador o limpia el almacenamiento, pierde la posibilidad de activar los
 * avisos de ese envío; es el mismo trato que con la autoría de las anotaciones.
 */
function claveRemitente(token: string): string {
  return `ploter:share:${token}:remitente`
}

/** Guarda el secreto tras subir. Lo llama el flujo de subida de la landing. */
export function recordarQueSoyElRemitente(token: string, secreto: string): void {
  try {
    localStorage.setItem(claveRemitente(token), secreto)
  } catch {
    // Sin almacenamiento no se podrán activar los avisos de este envío. Se
    // prefiere eso a ofrecer un campo que después falla.
  }
}

/** ¿Fue este navegador el que subió este envío? */
export function soyElRemitente(token: string | undefined): string | null {
  if (!token) return null
  try {
    return localStorage.getItem(claveRemitente(token))
  } catch {
    return null
  }
}

export function avisoYaActivado(token: string | undefined): boolean {
  if (!token) return false
  try {
    return localStorage.getItem(clave(token)) !== null
  } catch {
    return false
  }
}

export async function activarAviso(token: string, email: string): Promise<void> {
  const senderKey = soyElRemitente(token)
  if (!senderKey) throw new Error('sin_clave_de_remitente')

  const r = await api<{ ok: boolean; guardado: boolean }>('/share-notify', {
    method: 'POST',
    credentials: false,
    body: { token, email, senderKey },
  })

  // El servidor responde `guardado: false` si ya había una dirección apuntada.
  // Antes se ignoraba y se pintaba el check verde igual, así que el usuario se
  // quedaba convencido de que iba a recibir avisos que no iban a llegar. Un
  // fallo silencioso sin ninguna pantalla donde descubrirlo.
  if (!r.guardado) throw new Error('ya_habia_direccion')

  try {
    // Solo un '1': el único lector comprueba que exista. Guardar el correo
    // entero dejaba la dirección del anterior en un ordenador compartido.
    localStorage.setItem(clave(token), '1')
  } catch {
    // Modo privado: se pierde el recuerdo, no el aviso. Volverá a ofrecerlo.
  }
}
